import React, { useEffect } from 'react';
import { Select, SelectItem, Selection } from '@nextui-org/react';
import { MarketData, Settlement } from 'common/src/utils/types';
import { useTranslation } from '../../hooks/useTranslation';

interface ColorFilterProps {
  data: MarketData[] | Settlement[];
  selectedColor: string | string[];
  onSelectionChange: (value: string[]) => void;
  selectionMode?: 'single' | 'multiple';
  label?: string;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  defaultSelectedKeys?: string[];
  defaultSelectAll?: boolean;
  hideAllOption?: boolean;
  // Sin selección no se filtra por color: se muestra "Todos los colores".
  emptyMeansAll?: boolean;
}

const ColorFilter: React.FC<ColorFilterProps> = ({
  data,
  selectedColor,
  onSelectionChange,
  selectionMode = 'single',
  label,
  className = 'min-w-32',
  size = 'sm',
  defaultSelectedKeys,
  defaultSelectAll = false,
  hideAllOption = false,
  emptyMeansAll = false,
}) => {
  const { t } = useTranslation('common');
  const finalLabel = label || t('Color');
  const colors = React.useMemo(() => {
    // Use raw color codes directly. Previously this bucketed values into
    // Dark / Light / Dark Dark which masked real values like '2DD' and caused
    // filter mismatches.
    const colorCount = data.reduce((acc, item) => {
      if ('color' in item) {
        const color = item.color || 'Sin Color';
        acc[color] = (acc[color] || 0) + 1;
      } else if ('settlement_details' in item) {
        item.settlement_details?.forEach((detail) => {
          const color = detail.color || 'Sin Color';
          acc[color] = (acc[color] || 0) + 1;
        });
      }
      return acc;
    }, {} as Record<string, number>);

    return Object.entries(colorCount)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([color]) => color);
  }, [data]);

  useEffect(() => {
    if (
      selectionMode === 'multiple' &&
      defaultSelectAll &&
      colors.length > 0
    ) {
      onSelectionChange(colors);
    }
  }, []);

  if (colors.length === 0) {
    return null;
  }



  const handleSelectionChange = (keys: Selection) => {
    if (selectionMode === 'multiple') {
      const keysArray = Array.from(keys);

      if (keysArray.includes('all')) {
        // If 'all' is selected and there were previous selections, clear all
        if (Array.isArray(selectedColor) && selectedColor.length > 0) {
          onSelectionChange([]);
        } else {
          // Otherwise, select all formats
          onSelectionChange(colors);
        }
      } else {
        // Normal selection behavior for individual items
        onSelectionChange(keysArray as string[]);
      }
    } else {
      const selectedKeys = Array.from(keys);
      onSelectionChange(selectedKeys as string[]);
    }
  };

  return (
    <Select
      label={finalLabel}
      selectedKeys={
        selectionMode === 'multiple'
          ? Array.isArray(selectedColor)
            ? new Set(selectedColor)
            : new Set([])
          : new Set([selectedColor as string])
      }
      onSelectionChange={handleSelectionChange}
      selectionMode={selectionMode}
      defaultSelectedKeys={defaultSelectedKeys && new Set(defaultSelectedKeys)}
      className={className}
      size={size}
      placeholder={emptyMeansAll ? t('Todos los colores') : undefined}
      aria-label={`${t('Selecciona color')}`}
      renderValue={(items) => {
        if (
          selectionMode === 'multiple' &&
          Array.isArray(selectedColor) &&
          selectedColor.length === colors.length
        ) {
          return (
            <div className='flex items-center gap-2'>{t('Todos los colores')}</div>
          );
        }
        if (Array.isArray(selectedColor) && selectedColor.length > 0) {
          return (
            <div className='flex items-center gap-2'>
              {selectedColor.join(', ')}
            </div>
          );
        }
        return items;
      }}
    >
      {selectionMode === 'single' && !hideAllOption && (
        <SelectItem key='all' value='all' textValue={t('Todos')}>
          {t('Todos')}
        </SelectItem>
      )}
      {selectionMode === 'multiple' && !hideAllOption && (
        <SelectItem key='all' value='all' textValue={t('Todos')}>
          <div className='flex justify-between items-center'>
            <span>
              {Array.isArray(selectedColor) && selectedColor.length > 0
                ? t('Deseleccionar todos')
                : t('Seleccionar todos')}
            </span>
          </div>
        </SelectItem>
      )}

      {colors.map((color) => (
        <SelectItem key={color} value={color} textValue={color}>
          {color}
        </SelectItem>
      ))}
    </Select>
  );
};

export default React.memo(ColorFilter);
